import { useMemo, useState, useEffect } from 'react';
import type {
    VehicleSearchFilters,
    VehicleDerivative,
    FilteredOptionsResponse,
} from '../types/manualsearch.types';
import { useVehicleSearchContext } from './useVehicleSearchContext';
import {
    useFilteredOptionsQuery,
    useManualVehicleSearchQuery,
    usePrefetchVehicleData,
} from './useVehicleSearchQueries';
import { transformFilteredOptions, manualSearchResultMessage } from '../utils';

export const useManualVehicleSearchWithQuery = () => {
    const { selectVehicle } = useVehicleSearchContext();
    const { prefetchFilteredOptions } = usePrefetchVehicleData();

    const [message, setMessage] = useState<string>('');
    const [currentSelections, setCurrentSelections] = useState<VehicleSearchFilters>({});
    const [selectedFilters, setSelectedFilters] = useState<VehicleSearchFilters>({});
    const [searchFilters, setSearchFilters] = useState<VehicleSearchFilters>({});
    const [isSearchEnabled, setIsSearchEnabled] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [availableOptions, setAvailableOptions] = useState<
        FilteredOptionsResponse['available_options']
    >({});

    // Query for getting filtered options (makes, models, etc.)
    const {
        data: filteredOptionsData,
        isLoading: isOptionsLoading,
        error: optionsError,
    } = useFilteredOptionsQuery(currentSelections);

    // Use query instead of mutation for caching
    const {
        data: searchResult,
        isLoading: isSearchLoading,
        isFetching,
    } = useManualVehicleSearchQuery(searchFilters, isSearchEnabled);

    // Transform the API response into the format expected by components
    useEffect(() => {
        setAvailableOptions((prevOptions) => {
            const newOptions = { ...prevOptions };
            // Update each property explicitly to maintain proper types
            if (filteredOptionsData?.available_options.makes) {
                newOptions.makes = filteredOptionsData?.available_options.makes;
            }
            if (filteredOptionsData?.available_options.models) {
                newOptions.models = filteredOptionsData?.available_options.models;
            }
            if (filteredOptionsData?.available_options.engine_sizes) {
                newOptions.engine_sizes = filteredOptionsData?.available_options.engine_sizes;
            }
            if (filteredOptionsData?.available_options.transmissions) {
                newOptions.transmissions = filteredOptionsData?.available_options.transmissions;
            }
            if (filteredOptionsData?.available_options.fuel_types) {
                newOptions.fuel_types = filteredOptionsData?.available_options.fuel_types;
            }
            if (filteredOptionsData?.available_options.years) {
                newOptions.years = filteredOptionsData?.available_options.years;
            }
            return newOptions;
        });
    }, [filteredOptionsData]);

    const derivativeOptions = useMemo(() => {
        if (!availableOptions) {
            return {
                makes: [],
                models: [],
                engine_sizes: [],
                transmissions: [],
                fuel_types: [],
                years: [],
            };
        }
        // Use the utility function to transform options
        const newOutput = transformFilteredOptions(availableOptions);
        return newOutput;
    }, [availableOptions]);

    // Handle filter changes with caching
    const onFilterChange = async (newFilters: VehicleSearchFilters) => {
        setCurrentSelections(newFilters);

        // Clear derivatives when filters change
        if (newFilters.make_id !== selectedFilters.make_id) {
            newFilters = { make_id: newFilters.make_id };
            setCurrentSelections(newFilters);
        }
        if (newFilters.model_id !== selectedFilters.model_id) {
            newFilters = { make_id: newFilters.make_id, model_id: newFilters.model_id };
            setCurrentSelections(newFilters);
        }
        setSelectedFilters(newFilters);

        // Prefetch next level of options
        if (newFilters.make_id && !newFilters.model_id) {
            prefetchFilteredOptions({ make_id: newFilters.make_id });
        }
        if (newFilters.make_id && newFilters.model_id) {
            prefetchFilteredOptions({
                make_id: newFilters.make_id,
                model_id: newFilters.model_id,
            });
        }
    };

    const onDerivativeSearch = async (filters: VehicleSearchFilters) => {
        setHasSearched(true);
        setSearchFilters(filters);
        setIsSearchEnabled(true); // Triggers the cached query
    };

    // Handle vehicle selection
    const handleVehicleSelect = (vehicle: VehicleDerivative) => {
        selectVehicle(vehicle);
    };

    // Initialize with default message
    useEffect(() => {
        if (searchResult && isSearchEnabled) {
            setMessage(manualSearchResultMessage(searchResult));

            setIsSearchEnabled(false);
        }
    }, [searchResult, isSearchEnabled]);

    return {
        // Loading states
        isLoading: isOptionsLoading,
        isSearchLoading: isSearchLoading || isFetching,

        // Data
        message,
        hasSearched,
        derivativeOptions,
        currentSelections,
        vehicleDerivatives: searchResult?.vehicles ?? [],

        // Actions
        onSearch: onDerivativeSearch,
        onFilterChange,
        onVehicleSelect: handleVehicleSelect,

        // Raw query data for advanced use cases
        filteredOptionsQuery: {
            data: filteredOptionsData,
            isLoading: isOptionsLoading,
            error: optionsError,
        },
    };
};
